import React, { createContext, useContext, useEffect, useState } from "react";
import { trackEvent } from "./analytics";

const STORAGE_KEY = "yuva-lang";

// Flat dot-notation keys. Missing hi → falls back to en.
const TRANSLATIONS = {
  en: {
    // Nav & header
    "nav.home": "Home",
    "nav.about": "About",
    "nav.treatments": "Treatments",
    "nav.journal": "Journal",
    "nav.contact": "Contact",
    "nav.consultations": "Consultations",
    "cta.book": "Schedule a Visit",
    "cta.bookShort": "Visit the clinic",
    "cta.explore": "Explore treatments",
    "cta.readMore": "Read more",
    "cta.consult": "Consult",
    "cta.read": "Read",
    "cta.viewAll": "View all",
    "cta.getDirections": "Get directions",
    "cta.chat": "Chat with us",
    "cta.chatWhatsapp": "Chat on WhatsApp",
    "cta.sendMessage": "Send message",
    "cta.sending": "Sending…",
    "cta.request": "Schedule a counselling session with the Doctor",
    "cta.visitClinic": "Visit the clinic",
    "lang.toggle": "हिंदी",
    "lang.ariaLabel": "Switch language",

    // Hero (Home)
    "hero.overline": "Since 2008 · Prayagraj",
    "hero.title.l1": "Restoring",
    "hero.title.l2": "physical and mental",
    "hero.title.l3": "strength,",
    "hero.title.l4": "naturally.",
    "hero.subtitle": "Prayagraj's most trusted specialised Ayurvedic clinic for men's sexual health. Classical Ayurvedic medicine, 100% confidential care, natural treatment — no side effects, no shortcuts.",
    "hero.stat.iso": "ISO Certified",
    "hero.stat.patients": "50,000+ Satisfied Patients",
    "hero.stat.since": "Since 2008",
    "hero.promise.label": "Patient promise",
    "hero.promise.text": "Drug-free healing. Root-cause care. Real transformation.",

    // Home — sections
    "story.overline": "Our story",
    "story.title.l1": "A specialised",
    "story.title.l2": "Ayurvedic practice",
    "story.title.l3": "for men.",
    "story.title.l4": "",
    "story.p1": "Yuva Ayurvedic Clinic Private Limited was founded in 2008 in Prayagraj with a single intention — to give men a trusted, completely confidential place to treat sexual health concerns through authentic classical Ayurvedic medicine and natural treatments.",
    "story.p2": "We believe a meaningful consultation is not a fifteen-minute transaction. It is a patient hearing of your story, a careful reading of your pulse, and a personalised Ayurvedic protocol that respects your life. Every medicine we prescribe is traceable to a classical Ayurvedic text and dispensed from our in-house pharmacy by trained Ayurvedic physicians.",
    "story.stat.years": "Years of experience",
    "story.stat.patients": "Satisfied patients",
    "story.stat.discretion": "Discretion",
    "specs.overline": "Our specialities",
    "specs.title.l1": "Classical Ayurvedic care",
    "specs.title.l2": "for men's sexual health.",
    "specs.subtitle": "Confidential, root-cause Ayurvedic treatment for mardana kamzori, shighrapatan, napunsakta, gupt rog and every other men's sexual concern — diagnosed privately, treated without judgement.",
    "specs.tag": "Speciality",
    "pillars.overline": "How we heal",
    "pillars.title.l1": "Four pillars.",
    "pillars.title.l2": "One philosophy.",
    "consultCta.overline": "Consultations",
    "consultCta.title.l1": "A slow, careful",
    "consultCta.title.l2": "conversation",
    "consultCta.title.l3": "about you.",
    "consultCta.subtitle": "45 minutes. Private room. No rushed prescriptions. Walk out with a plan you can actually follow.",
    "voices.overline": "Patient voices",
    "voices.title": "What our patients",
    "voices.titleItalic": "say.",
    "facilities.overline": "Facilities",
    "facilities.title.l1": "A clinic that",
    "facilities.title.l2": "feels like",
    "facilities.title.l3": "home.",
    "journal.overline": "Journal",
    "journal.title.l1": "Notes on",
    "journal.title.l2": "living well.",

    // About
    "about.overline": "About the clinic",
    "about.title.l1": "An Ayurvedic",
    "about.title.l2": "practice built on",
    "about.title.l3": "listening.",

    // Treatments page
    "tx.overline": "Treatments",
    "tx.title.l1": "Classical Ayurvedic care,",
    "tx.title.l2": "for every men's",
    "tx.title.l3": "sexual health concern.",
    "tx.subtitle": "Each Ayurvedic protocol is designed around your prakriti (constitution), vikriti (current imbalance) and the life you're actually living. Drug-free, confidential, traceable to classical texts.",
    "tx.therapyLabel": "Therapy",
    "tx.pillars.overline": "Core pillars",
    "tx.pillars.title.l1": "Every protocol rests on",
    "tx.pillars.title.l2": "four foundations.",

    // Consultations booking flow
    "consult.overline": "Consultations",
    "consult.title.l1": "Book a slow",
    "consult.title.l2": "conversation",
    "consult.title.l3": "about you.",
    "consult.blurb": "Your first consultation is 45 minutes in a private room. Bring your story, any recent reports, and any question you've been carrying. We'll map your prakriti, review your lifestyle, and design a drug-free therapy plan tailored to you.",
    "consult.bullet.visit.h": "45 minute first visit",
    "consult.bullet.visit.p": "Thorough history, nadi pariksha, therapy protocol.",
    "consult.bullet.drugfree.h": "Classical Ayurvedic protocols",
    "consult.bullet.drugfree.p": "Herbal rasayanas, vajikarana therapies, diet and lifestyle — prepared in our in-house Ayurvedic pharmacy.",
    "consult.bullet.unhurried.h": "Unhurried care",
    "consult.bullet.unhurried.p": "We treat root causes, not symptoms.",
    "consult.step1": "Step 1 · Select a date",
    "consult.step2": "Step 2 · Pick a time",
    "consult.step3": "Step 3 · Your concern",
    "consult.step4": "Step 4 · Your details",
    "consult.placeholder.concern": "Select a concern",
    "consult.option.general": "General wellness consultation",
    "consult.option.detox": "Ayurvedic detox programme",
    "consult.field.name": "Full name",
    "consult.field.phone": "Phone",
    "consult.field.email": "Email",
    "consult.field.message": "Anything we should know?",
    "consult.field.message.placeholder": "Briefly share your concern — optional",
    "consult.disclaimer": "By booking you agree to a brief confirmation call from our team.",
    "consult.err.date": "Please select a date",
    "consult.err.time": "Please select a time",
    "consult.err.concern": "Please select your concern",
    "consult.err.details": "Please fill in name, email and phone",
    "consult.err.rate": "Too many requests. Please try again in a few minutes.",
    "consult.err.generic": "Something went wrong. Please try again.",
    "consult.success.toast": "Consultation request received. We'll confirm shortly.",
    "consult.success.title": "Thank you,",
    "consult.success.friend": "friend",
    "consult.success.body.1": "Your consultation request for",
    "consult.success.body.2": "at",
    "consult.success.body.3": "has been received. Our team will call you to confirm within 24 hours.",
    "consult.success.another": "Book another",

    // What to expect on your visit
    "expect.overline": "Your first visit",
    "expect.title.l1": "What to expect",
    "expect.title.l2": "at the clinic.",
    "expect.sub": "This is a counselling-led, in-person Ayurvedic practice. Here's exactly how your first visit works — unhurried, private, and rooted in classical method.",
    "expect.step1.h": "Book your slot",
    "expect.step1.p": "Pick a date and time online, or call us. We confirm within 24 hours on WhatsApp.",
    "expect.step2.h": "Visit the clinic",
    "expect.step2.p": "Arrive at our Prayagraj clinic. You are received privately — no waiting-room exposure, no rushed check-ins.",
    "expect.step3.h": "45-min private counselling",
    "expect.step3.p": "You sit one-to-one with the Ayurvedic doctor. Full history, nadi pariksha (pulse reading), and an open conversation about your concern.",
    "expect.step4.h": "Personalised Ayurvedic plan",
    "expect.step4.p": "You leave with a written Ayurvedic protocol — classical rasayanas, diet and lifestyle — tailored to your prakriti and life.",

    // First-visit FAQ
    "faq.overline": "First visit · FAQ",
    "faq.title.l1": "Before you",
    "faq.title.l2": "come in.",
    "faq.q1": "Do I need to bring any reports?",
    "faq.a1": "No prior reports are required. If you already have recent blood work, USG or semen-analysis reports, bring them — it helps the doctor map your picture faster. Otherwise, we start from a careful history and nadi pariksha.",
    "faq.q2": "How long will the first visit take?",
    "faq.a2": "Plan for around 45 minutes of private one-to-one counselling with the doctor. Complicated cases may run a little longer — we never rush a first visit.",
    "faq.q3": "Is it completely private?",
    "faq.a3": "Yes. All consultations happen in a closed, private room. Your name, concern and treatment plan stay strictly between you and the doctor — no waiting-room disclosure.",
    "faq.q4": "Is the medicine included in the visit?",
    "faq.a4": "Your counselling and personalised plan are handed over on the day of the visit. Any recommended Ayurvedic medicines are prepared separately at the clinic pharmacy and are entirely optional — you decide what to take home.",

    // Contact page
    "contact.overline": "Contact",
    "contact.title.l1": "Write to us.",
    "contact.title.l2": "We reply",
    "contact.title.l3": "personally.",
    "contact.blurb": "Reach out for bookings, follow-ups or questions. For urgent queries, WhatsApp is fastest.",
    "contact.block.address": "Clinic address",
    "contact.block.address.value": "Yuva Ayurvedic Clinic Private Limited,\n207/53, 2nd Floor, Mahatma Gandhi Marg, Subhash Chauraha, Civil Lines,\nPrayagraj, Uttar Pradesh 211001",
    "contact.block.call": "Call",
    "contact.block.email": "Email",
    "contact.block.whatsapp": "WhatsApp",
    "contact.field.name": "Name *",
    "contact.field.phone": "Phone",
    "contact.field.email": "Email *",
    "contact.field.subject": "Subject",
    "contact.field.subject.placeholder": "General enquiry",
    "contact.field.message": "Message *",
    "contact.err.required": "Please fill required fields",
    "contact.err.rate": "Too many requests. Please try again in a few minutes.",
    "contact.err.generic": "Something went wrong",
    "contact.success": "Message received. We'll be in touch soon.",
    "contact.map.overline": "Find us",
    "contact.map.title.l1": "Visit the",
    "contact.map.title.l2": "clinic.",

    // Blog
    "blog.overline": "Journal",
    "blog.title": "Notes on Ayurveda, intimacy, and a life lived well.",
    "blog.titleItalic": "intimacy,",
    "blog.back": "Back to Journal",

    // Footer
    "footer.overline": "Since 2008 · Prayagraj",
    "footer.tagline": "A specialised, confidential Ayurvedic practice for men's sexual wellness — classical medicine, unhurried consultations, deep respect for each patient's story.",
    "footer.nav.label": "Navigate",
    "footer.visit.label": "Visit",
    "footer.admin": "Admin",
    "footer.credit": "Handcrafted in Prayagraj",
    "footer.rights": "All rights reserved.",

    // WhatsApp float
    "whatsapp.aria": "Chat on WhatsApp",

    // Hinglish SEO section (visible copy — targets Prayagraj patients who search in Hindi via English letters)
    "hindi.overline": "Aap ki bhasha mein",
    "hindi.title": "Prayagraj Mein Bharosemand Ayurvedic Ilaaj",
    "hindi.bullet1": "Yuva Ayurvedic Clinic mein Prayagraj ke sabse anubhavi doctor se milein",
    "hindi.bullet2": "Mardana kamzori, shighrapatan, aur gupt rog ka 100% natural Ayurvedic ilaaj",
    "hindi.bullet3": "18+ saal ka anubhav, 50,000+ santusht marij",
    "hindi.bullet4": "Aapki baat sirf aapke aur hamare beech rehti hai — poori privacy guaranteed",
    "hindi.bullet5": "Abhi WhatsApp karein ya appointment book karein",
    "hindi.cta.book": "Appointment book karein",
    "hindi.cta.whatsapp": "WhatsApp karein",

    // Trust pillars
    "trust.overline": "Bharosa, 18+ saal se",
    "trust.title.l1": "Why 50,000+ men trust",
    "trust.title.l2": "Yuva Ayurvedic.",
    "trust.p1.h": "100% Confidential",
    "trust.p1.p": "Every consultation is held in a closed, private room. Your name, concern and plan stay strictly between you and the doctor.",
    "trust.p2.h": "18+ years of experience",
    "trust.p2.p": "Practising classical Ayurvedic men's sexual wellness in Prayagraj since 2008, with a senior Ayurvedic physician leading every case.",
    "trust.p3.h": "50,000+ satisfied patients",
    "trust.p3.p": "Men from across Uttar Pradesh, India and NRI families abroad have healed with us. Discreet, documented and deeply respected.",
    "trust.p4.h": "Natural, no side effects",
    "trust.p4.p": "Only classical Ayurvedic herbal rasayanas, vajikarana formulations and prakriti-based lifestyle — never synthetic, never harsh.",
    "trust.p5.h": "Online consultation for NRI",
    "trust.p5.p": "Patients outside India can consult our Ayurvedic doctor over WhatsApp and video call, with medicines couriered worldwide.",

    // NRI / International online consultation
    "nri.overline": "NRI & International patients",
    "nri.title.l1": "Online Consultation",
    "nri.title.l2": "Available Worldwide",
    "nri.sub": "Living outside India? We offer confidential online consultations via WhatsApp and video call for NRI and international patients. Our Ayurvedic doctors have helped patients from USA, UK, Canada, UAE, Australia and across the world. Book your private online consultation today.",
    "nri.countries": "USA · UK · Canada · UAE · Australia · Singapore · Germany · Saudi Arabia · worldwide",
    "nri.bullet1": "Private video consultation with the Ayurvedic doctor",
    "nri.bullet2": "Available in English and Hindi, time-zone friendly",
    "nri.bullet3": "Classical Ayurvedic medicines couriered worldwide",
    "nri.bullet4": "Encrypted WhatsApp follow-ups, direct with the doctor",
    "nri.cta.whatsapp": "WhatsApp for international consultation",
    "nri.cta.book": "Request video consultation",

    // Common
    "common.loading": "Loading…",
    "common.notFound": "Article not found.",
  },
  hi: {
    // Nav & header
    "nav.home": "मुख्य पृष्ठ",
    "nav.about": "हमारे बारे में",
    "nav.treatments": "उपचार",
    "nav.journal": "जर्नल",
    "nav.contact": "संपर्क",
    "nav.consultations": "परामर्श",
    "cta.book": "विजिट बुक करें",
    "cta.bookShort": "क्लिनिक पधारें",
    "cta.explore": "उपचार देखें",
    "cta.readMore": "और पढ़ें",
    "cta.consult": "परामर्श",
    "cta.read": "पढ़ें",
    "cta.viewAll": "सभी देखें",
    "cta.getDirections": "रास्ता देखें",
    "cta.chat": "हमसे बात करें",
    "cta.chatWhatsapp": "WhatsApp पर चैट करें",
    "cta.sendMessage": "संदेश भेजें",
    "cta.sending": "भेजा जा रहा है…",
    "cta.request": "डॉक्टर से काउंसलिंग सत्र बुक करें",
    "cta.visitClinic": "क्लिनिक पधारें",
    "lang.toggle": "English",
    "lang.ariaLabel": "भाषा बदलें",

    // Hero
    "hero.overline": "स्थापित 2008 · प्रयागराज",
    "hero.title.l1": "शारीरिक और",
    "hero.title.l2": "मानसिक शक्ति",
    "hero.title.l3": "को,",
    "hero.title.l4": "प्राकृतिक रूप से बहाल करें।",
    "hero.subtitle": "प्रयागराज का सबसे भरोसेमंद विशेषज्ञ आयुर्वेदिक क्लिनिक — पुरुषों की गुप्त समस्याओं का शास्त्रीय आयुर्वेदिक इलाज। 100% गोपनीय, प्राकृतिक उपचार, कोई साइड इफेक्ट नहीं।",
    "hero.stat.iso": "ISO प्रमाणित",
    "hero.stat.patients": "50,000+ संतुष्ट रोगी",
    "hero.stat.since": "2008 से",
    "hero.promise.label": "रोगी को हमारा वचन",
    "hero.promise.text": "दवा-मुक्त उपचार। जड़-कारण चिकित्सा। वास्तविक परिवर्तन।",

    // Home
    "story.overline": "हमारी कहानी",
    "story.title.l1": "योग चिकित्सा और",
    "story.title.l2": "प्राकृतिक चिकित्सा",
    "story.title.l3": "का",
    "story.title.l4": "शांत अभ्यास।",
    "story.p1": "युवा आयुर्वेदिक क्लिनिक प्राइवेट लिमिटेड की स्थापना 2008 में प्रयागराज में एक ही उद्देश्य से हुई थी — योग चिकित्सा, प्राकृतिक चिकित्सा और शास्त्रीय आयुर्वेदिक डिटॉक्स कार्यक्रमों के माध्यम से प्रामाणिक, शोध-आधारित देखभाल सभी तक पहुँचाना।",
    "story.p2": "हमारा मानना है कि सार्थक परामर्श पंद्रह मिनट का लेन-देन नहीं है। यह आपकी कहानी को धैर्यपूर्वक सुनना, आपकी नाड़ी का सावधानीपूर्वक निरीक्षण, और एक दवा-मुक्त प्रोटोकॉल है जो आपके जीवन के अनुसार बने। हमारे द्वारा दी गई प्रत्येक चिकित्सा शास्त्रीय ग्रंथ से जुड़ी है।",
    "story.stat.years": "वर्षों का अनुभव",
    "story.stat.patients": "संतुष्ट रोगी",
    "story.stat.discretion": "गोपनीयता",
    "specs.overline": "हमारी विशेषज्ञताएँ",
    "specs.title.l1": "पुरुष यौन स्वास्थ्य के लिए",
    "specs.title.l2": "शास्त्रीय आयुर्वेदिक देखभाल।",
    "specs.subtitle": "मर्दाना कमज़ोरी, शीघ्रपतन, नपुंसकता, गुप्त रोग और पुरुषों की हर यौन समस्या का गोपनीय, जड़-कारण आयुर्वेदिक इलाज — निजता में निदान, बिना निर्णय उपचार।",
    "specs.tag": "विशेषता",
    "pillars.overline": "हम कैसे ठीक करते हैं",
    "pillars.title.l1": "चार स्तंभ।",
    "pillars.title.l2": "एक दर्शन।",
    "consultCta.overline": "परामर्श",
    "consultCta.title.l1": "आपके बारे में",
    "consultCta.title.l2": "एक धीमी, सावधान",
    "consultCta.title.l3": "बातचीत।",
    "consultCta.subtitle": "45 मिनट। निजी कक्ष। कोई जल्दबाजी नहीं। एक ऐसी योजना लेकर जाएँ जिसे आप वास्तव में अपना सकें।",
    "voices.overline": "रोगियों की आवाज़",
    "voices.title": "हमारे रोगी क्या",
    "voices.titleItalic": "कहते हैं।",
    "facilities.overline": "सुविधाएं",
    "facilities.title.l1": "एक क्लिनिक जो",
    "facilities.title.l2": "घर जैसा",
    "facilities.title.l3": "लगे।",
    "journal.overline": "जर्नल",
    "journal.title.l1": "अच्छा जीवन जीने पर",
    "journal.title.l2": "विचार।",

    // About
    "about.overline": "क्लिनिक के बारे में",
    "about.title.l1": "सुनने पर",
    "about.title.l2": "आधारित एक",
    "about.title.l3": "आयुर्वेदिक अभ्यास।",

    // Treatments
    "tx.overline": "उपचार",
    "tx.title.l1": "पुरुषों की हर यौन",
    "tx.title.l2": "स्वास्थ्य समस्या के लिए",
    "tx.title.l3": "शास्त्रीय आयुर्वेदिक देखभाल।",
    "tx.subtitle": "प्रत्येक आयुर्वेदिक प्रोटोकॉल आपकी प्रकृति, वर्तमान विकृति और वास्तविक जीवनशैली के अनुसार बनाया जाता है। गोपनीय, शास्त्रीय ग्रंथों से जुड़ा, बिना साइड इफेक्ट।",
    "tx.therapyLabel": "चिकित्सा",
    "tx.pillars.overline": "मुख्य स्तंभ",
    "tx.pillars.title.l1": "प्रत्येक प्रोटोकॉल",
    "tx.pillars.title.l2": "चार नींवों पर टिका है।",

    // Consultations
    "consult.overline": "परामर्श",
    "consult.title.l1": "आपके बारे में",
    "consult.title.l2": "एक धीमी",
    "consult.title.l3": "बातचीत बुक करें।",
    "consult.blurb": "आपका पहला परामर्श एक निजी कक्ष में 45 मिनट का होगा। अपनी कहानी, हाल की रिपोर्टें, और अपने प्रश्न साथ लाइए। हम आपकी प्रकृति का मूल्यांकन करेंगे, जीवनशैली की समीक्षा करेंगे, और आपके लिए एक अनुकूलित दवा-मुक्त चिकित्सा योजना बनाएँगे।",
    "consult.bullet.visit.h": "45 मिनट की पहली भेंट",
    "consult.bullet.visit.p": "पूरा इतिहास, नाड़ी परीक्षा, चिकित्सा प्रोटोकॉल।",
    "consult.bullet.drugfree.h": "शास्त्रीय आयुर्वेदिक प्रोटोकॉल",
    "consult.bullet.drugfree.p": "हर्बल रसायन, वाजीकरण चिकित्सा, आहार व जीवनशैली — इन-हाउस आयुर्वेदिक फार्मेसी में तैयार।",
    "consult.bullet.unhurried.h": "बिना जल्दबाजी देखभाल",
    "consult.bullet.unhurried.p": "हम लक्षण नहीं, जड़ कारण का इलाज करते हैं।",
    "consult.step1": "चरण 1 · तारीख चुनें",
    "consult.step2": "चरण 2 · समय चुनें",
    "consult.step3": "चरण 3 · आपकी समस्या",
    "consult.step4": "चरण 4 · आपकी जानकारी",
    "consult.placeholder.concern": "समस्या चुनें",
    "consult.option.general": "सामान्य स्वास्थ्य परामर्श",
    "consult.option.detox": "आयुर्वेदिक डिटॉक्स कार्यक्रम",
    "consult.field.name": "पूरा नाम",
    "consult.field.phone": "फ़ोन",
    "consult.field.email": "ईमेल",
    "consult.field.message": "क्या हमें कुछ बताना चाहेंगे?",
    "consult.field.message.placeholder": "अपनी समस्या संक्षेप में बताएँ — वैकल्पिक",
    "consult.disclaimer": "बुकिंग करके आप हमारी टीम से पुष्टि कॉल के लिए सहमत होते हैं।",
    "consult.err.date": "कृपया एक तारीख चुनें",
    "consult.err.time": "कृपया समय चुनें",
    "consult.err.concern": "कृपया अपनी समस्या चुनें",
    "consult.err.details": "कृपया नाम, ईमेल और फ़ोन भरें",
    "consult.err.rate": "बहुत अधिक अनुरोध। कृपया कुछ मिनट बाद पुनः प्रयास करें।",
    "consult.err.generic": "कुछ गलत हुआ। कृपया पुनः प्रयास करें।",
    "consult.success.toast": "परामर्श अनुरोध प्राप्त हो गया। हम जल्द पुष्टि करेंगे।",
    "consult.success.title": "धन्यवाद,",
    "consult.success.friend": "मित्र",
    "consult.success.body.1": "आपका",
    "consult.success.body.2": "को",
    "consult.success.body.3": "के लिए परामर्श अनुरोध प्राप्त हो गया है। हमारी टीम 24 घंटों में पुष्टि हेतु कॉल करेगी।",
    "consult.success.another": "एक और बुक करें",

    // What to expect on your visit
    "expect.overline": "आपकी पहली विज़िट",
    "expect.title.l1": "क्लिनिक पर क्या",
    "expect.title.l2": "अपेक्षा करें।",
    "expect.sub": "यह काउंसलिंग-आधारित, व्यक्तिगत रूप से आने वाला आयुर्वेदिक अभ्यास है। आपकी पहली विज़िट कैसी होगी — बिना जल्दबाजी, निजी, और शास्त्रीय विधि पर आधारित।",
    "expect.step1.h": "अपना स्लॉट बुक करें",
    "expect.step1.p": "ऑनलाइन तारीख-समय चुनें या हमें कॉल करें। हम 24 घंटे में WhatsApp पर पुष्टि करते हैं।",
    "expect.step2.h": "क्लिनिक पधारें",
    "expect.step2.p": "हमारे प्रयागराज क्लिनिक पहुँचें। आपका स्वागत निजी रूप से होता है — न वेटिंग-रूम, न जल्दबाजी का चेक-इन।",
    "expect.step3.h": "45 मिनट निजी काउंसलिंग",
    "expect.step3.p": "आप आयुर्वेदिक डॉक्टर के साथ आमने-सामने बैठते हैं। पूरा इतिहास, नाड़ी परीक्षा, और आपकी समस्या पर खुली बातचीत।",
    "expect.step4.h": "व्यक्तिगत आयुर्वेदिक योजना",
    "expect.step4.p": "आप एक लिखित, दवा-मुक्त आयुर्वेदिक प्रोटोकॉल लेकर जाते हैं — जड़ी-बूटी, योग, आहार व जीवनशैली — आपकी प्रकृति व जीवन के अनुसार।",

    // First-visit FAQ
    "faq.overline": "पहली विज़िट · FAQ",
    "faq.title.l1": "आने से पहले",
    "faq.title.l2": "जानें।",
    "faq.q1": "क्या मुझे कोई रिपोर्ट लानी होंगी?",
    "faq.a1": "पहले की रिपोर्ट ज़रूरी नहीं है। यदि हाल ही की ब्लड वर्क, USG या सीमेन-एनालिसिस रिपोर्ट हों, तो साथ लाइए — इससे डॉक्टर को आपकी स्थिति जल्दी समझने में मदद मिलती है। अन्यथा, हम इतिहास और नाड़ी परीक्षा से शुरू करते हैं।",
    "faq.q2": "पहली विज़िट कितनी देर की होगी?",
    "faq.a2": "डॉक्टर के साथ लगभग 45 मिनट की निजी काउंसलिंग का अनुमान रखें। जटिल मामले थोड़े लंबे चल सकते हैं — हम पहली विज़िट में कभी जल्दबाजी नहीं करते।",
    "faq.q3": "क्या यह पूरी तरह निजी है?",
    "faq.a3": "जी हाँ। सभी परामर्श बंद, निजी कक्ष में होते हैं। आपका नाम, समस्या और उपचार योजना आप और डॉक्टर के बीच ही रहती है — वेटिंग-रूम में कुछ नहीं बताया जाता।",
    "faq.q4": "क्या दवा विज़िट में शामिल है?",
    "faq.a4": "आपकी काउंसलिंग और व्यक्तिगत योजना विज़िट के दिन ही सौंप दी जाती है। कोई भी आयुर्वेदिक औषधि क्लिनिक फार्मेसी में अलग से तैयार होती है और पूरी तरह वैकल्पिक है — आप तय करते हैं कि क्या लेकर जाएँगे।",

    // Contact
    "contact.overline": "संपर्क",
    "contact.title.l1": "हमें लिखें।",
    "contact.title.l2": "हम",
    "contact.title.l3": "स्वयं उत्तर देते हैं।",
    "contact.blurb": "बुकिंग, फॉलो-अप या प्रश्नों के लिए संपर्क करें। जरूरी प्रश्नों के लिए WhatsApp सबसे तेज़ है।",
    "contact.block.address": "क्लिनिक का पता",
    "contact.block.address.value": "युवा आयुर्वेदिक क्लिनिक प्राइवेट लिमिटेड,\n207/53, द्वितीय तल, महात्मा गांधी मार्ग, सुभाष चौराहा, सिविल लाइंस,\nप्रयागराज, उत्तर प्रदेश 211001",
    "contact.block.call": "कॉल करें",
    "contact.block.email": "ईमेल",
    "contact.block.whatsapp": "WhatsApp",
    "contact.field.name": "नाम *",
    "contact.field.phone": "फ़ोन",
    "contact.field.email": "ईमेल *",
    "contact.field.subject": "विषय",
    "contact.field.subject.placeholder": "सामान्य पूछताछ",
    "contact.field.message": "संदेश *",
    "contact.err.required": "कृपया आवश्यक फ़ील्ड भरें",
    "contact.err.rate": "बहुत अधिक अनुरोध। कृपया कुछ मिनट बाद पुनः प्रयास करें।",
    "contact.err.generic": "कुछ गलत हुआ",
    "contact.success": "संदेश प्राप्त हुआ। हम जल्द संपर्क करेंगे।",
    "contact.map.overline": "हमें खोजें",
    "contact.map.title.l1": "क्लिनिक",
    "contact.map.title.l2": "पर पधारें।",

    // Blog
    "blog.overline": "जर्नल",
    "blog.title": "आयुर्वेद, आत्मीयता, और एक भरा-पूरा जीवन — पर विचार।",
    "blog.titleItalic": "आत्मीयता,",
    "blog.back": "जर्नल पर वापस",

    // Footer
    "footer.overline": "स्थापित 2008 · प्रयागराज",
    "footer.tagline": "पुरुष यौन स्वास्थ्य के लिए विशेषज्ञ, गोपनीय आयुर्वेदिक अभ्यास — शास्त्रीय औषधि, बिना जल्दबाजी परामर्श, और प्रत्येक रोगी की कहानी के प्रति गहरा सम्मान।",
    "footer.nav.label": "नेविगेट",
    "footer.visit.label": "पधारें",
    "footer.admin": "एडमिन",
    "footer.credit": "प्रयागराज में बनाया गया",
    "footer.rights": "सर्वाधिकार सुरक्षित।",

    // WhatsApp
    "whatsapp.aria": "WhatsApp पर चैट करें",

    // Hinglish SEO section
    "hindi.overline": "आप की भाषा में",
    "hindi.title": "प्रयागराज में भरोसेमंद आयुर्वेदिक इलाज",
    "hindi.bullet1": "युवा आयुर्वेदिक क्लिनिक में प्रयागराज के सबसे अनुभवी डॉक्टर से मिलें",
    "hindi.bullet2": "मर्दाना कमज़ोरी, शीघ्रपतन, और गुप्त रोग का 100% प्राकृतिक आयुर्वेदिक इलाज",
    "hindi.bullet3": "18+ वर्षों का अनुभव, 50,000+ संतुष्ट मरीज़",
    "hindi.bullet4": "आपकी बात केवल आपके और हमारे बीच रहती है — पूरी गोपनीयता सुनिश्चित",
    "hindi.bullet5": "अभी WhatsApp करें या अपॉइंटमेंट बुक करें",
    "hindi.cta.book": "अपॉइंटमेंट बुक करें",
    "hindi.cta.whatsapp": "WhatsApp करें",

    // Trust pillars
    "trust.overline": "18+ वर्षों का भरोसा",
    "trust.title.l1": "50,000+ पुरुष क्यों",
    "trust.title.l2": "युवा आयुर्वेदिक पर भरोसा करते हैं।",
    "trust.p1.h": "100% गोपनीय",
    "trust.p1.p": "हर परामर्श बंद, निजी कक्ष में होता है। आपका नाम, समस्या और योजना केवल आप व डॉक्टर के बीच रहती है।",
    "trust.p2.h": "18+ वर्षों का अनुभव",
    "trust.p2.p": "2008 से प्रयागराज में शास्त्रीय आयुर्वेदिक पुरुष यौन स्वास्थ्य का अभ्यास — प्रत्येक केस की निगरानी वरिष्ठ आयुर्वेदिक चिकित्सक द्वारा।",
    "trust.p3.h": "50,000+ संतुष्ट रोगी",
    "trust.p3.p": "उत्तर प्रदेश, पूरे भारत और विदेश में बसे NRI परिवारों के पुरुष हमारे यहाँ स्वस्थ हुए हैं। गोपनीय, दस्तावेज़ित और गहरा सम्मान।",
    "trust.p4.h": "प्राकृतिक, कोई साइड इफेक्ट नहीं",
    "trust.p4.p": "केवल शास्त्रीय आयुर्वेदिक हर्बल रसायन, वाजीकरण सूत्र और प्रकृति-आधारित जीवनशैली — कोई सिंथेटिक दवा नहीं, कोई कठोर उपचार नहीं।",
    "trust.p5.h": "NRI के लिए ऑनलाइन परामर्श",
    "trust.p5.p": "भारत के बाहर रहने वाले मरीज़ WhatsApp और वीडियो कॉल पर हमारे आयुर्वेदिक डॉक्टर से परामर्श कर सकते हैं, और दवाइयाँ विश्वभर में कूरियर की जाती हैं।",

    // NRI / International online consultation
    "nri.overline": "NRI और अंतर्राष्ट्रीय मरीज़",
    "nri.title.l1": "ऑनलाइन परामर्श",
    "nri.title.l2": "विश्व भर में उपलब्ध",
    "nri.sub": "क्या आप भारत के बाहर रहते हैं? NRI और अंतर्राष्ट्रीय मरीज़ों के लिए हम WhatsApp और वीडियो कॉल के माध्यम से गोपनीय ऑनलाइन परामर्श प्रदान करते हैं। हमारे आयुर्वेदिक डॉक्टरों ने USA, UK, Canada, UAE, Australia और पूरी दुनिया के मरीज़ों की मदद की है। आज ही अपना निजी ऑनलाइन परामर्श बुक करें।",
    "nri.countries": "USA · UK · Canada · UAE · Australia · Singapore · Germany · Saudi Arabia · विश्व भर में",
    "nri.bullet1": "आयुर्वेदिक डॉक्टर के साथ निजी वीडियो परामर्श",
    "nri.bullet2": "अंग्रेज़ी व हिंदी में उपलब्ध, टाइम-ज़ोन अनुकूल",
    "nri.bullet3": "शास्त्रीय आयुर्वेदिक दवाइयाँ विश्वभर में कूरियर",
    "nri.bullet4": "एन्क्रिप्टेड WhatsApp फॉलो-अप, सीधे डॉक्टर से",
    "nri.cta.whatsapp": "अंतर्राष्ट्रीय परामर्श के लिए WhatsApp",
    "nri.cta.book": "वीडियो परामर्श का अनुरोध करें",

    // Common
    "common.loading": "लोड हो रहा है…",
    "common.notFound": "लेख नहीं मिला।",
  },
};

const I18nContext = createContext({ lang: "en", setLang: () => {}, t: (k) => k });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || "en";
    } catch {
      return "en";
    }
  });

  const setLang = (l) => {
    setLangState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch { /* ignore */ }
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("lang", l);
    }
    trackEvent("hindi_toggle", { lang: l });
  };

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("lang", lang);
    }
  }, [lang]);

  const t = (key, fallback) => {
    const pack = TRANSLATIONS[lang] || TRANSLATIONS.en;
    return pack[key] ?? TRANSLATIONS.en[key] ?? fallback ?? key;
  };

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}

export function useT() {
  return useContext(I18nContext).t;
}

/**
 * Pick the localised field from a DB record.
 * Returns `obj[field + "_" + lang]` if present & non-empty, else `obj[field]`.
 * Usage: const title = usePick()(treatment, "title");
 */
export function usePick() {
  const { lang } = useContext(I18nContext);
  return (obj, field) => {
    if (!obj) return "";
    if (lang && lang !== "en") {
      const localised = obj[`${field}_${lang}`];
      if (localised && String(localised).trim()) return localised;
    }
    return obj[field] ?? "";
  };
}
